import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';
import logger from '../utils/logger';

// ─── Encryption key ───────────────────────────────────────────────────────────
// Must be set via .env as REACT_APP_ENCRYPTION_KEY.
// We intentionally do NOT provide a fallback — a missing key should fail loudly
// rather than silently encrypt with a publicly visible string.
const SECRET_KEY = process.env.REACT_APP_ENCRYPTION_KEY;
if (!SECRET_KEY) {
    throw new Error('[token] REACT_APP_ENCRYPTION_KEY is not set. Check your .env file.');
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const encryptData = (data, secretKey = SECRET_KEY) => {
    return CryptoJS.AES.encrypt(JSON.stringify(data), secretKey).toString();
};

export const decryptData = (encryptedData, secretKey = SECRET_KEY) => {
    try {
        if (!encryptedData) return null;
        const bytes = CryptoJS.AES.decrypt(encryptedData, secretKey);
        const str = bytes.toString(CryptoJS.enc.Utf8);
        return str ? JSON.parse(str) : null;
    } catch {
        return null;
    }
};

// ─── CSRF token ───────────────────────────────────────────────────────────────
// NOT httpOnly — must be readable by JS so it can be sent as a request header.

const setCsrfToken = (token) => {
    Cookies.set('csrfToken', token, {
        secure: process.env.NODE_ENV !== 'development',
        sameSite: 'lax',
    });
};

const getCsrfToken = () => Cookies.get('csrfToken');

const removeCsrfToken = () => Cookies.remove('csrfToken');

// ─── Full cleanup ─────────────────────────────────────────────────────────────
// Single source of truth for all logout cleanup.
// userLogout.js and API error handlers should call this — nothing else.

const removeUser = () => {
    const cookiesToRemove = [
        'userdetails',
        'user_id',
        'user_type',
        'org_id',
        'user_role',
        'user_role_name',
        'permissions',
        'org_logo',
        'org_name',
        'disabledashboard',
        'userLanguage',
        'csrfToken',
    ];
    cookiesToRemove.forEach((name) => Cookies.remove(name));
    sessionStorage.clear();
    localStorage.clear(); // clears redux-persist state and any other client-side storage
};

// ─── Logout (calls backend to clear httpOnly cookies) ─────────────────────────

const logout = async (axiosInstance) => {
    try {
        await axiosInstance.post('/api/v1/user/logout');
    } catch (error) {
        logger.error('[token.service] Logout API error:', error);
    } finally {
        removeUser();
    }
};

// ─── Export ───────────────────────────────────────────────────────────────────

const TokenService = {
    encryptData,
    decryptData,
    setCsrfToken,
    getCsrfToken,
    removeCsrfToken,
    removeUser,
    logout,
};

export default TokenService;
